import {
  createSlice,
  createAsyncThunk,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import {
  balancesParams,
  balancesFilters,
  ALL_BANKS,
} from '@utils/tables/params';
import { calculateFiltersCount } from '@utils/filters-helpers';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

const countFilters = (params) =>
  calculateFiltersCount(params, balancesFilters, ['bank_id']);

export const fetchBalances = createAsyncThunk(
  'balances/fetchBalances',
  async (_, { rejectWithValue, getState }) => {
    const { params, search } = getState().balances;

    const merged = { ...balancesParams, ...params };
    const query = {
      page: merged.page,
      per_page: merged.per_page,
      sort_by: merged.sort_by,
      sort_dir: merged.sort_dir,
      ...(search && { search }),
      ...Object.keys(balancesFilters).reduce((acc, key) => {
        if (key === 'bank_id' && merged[key] === ALL_BANKS) {
          return acc;
        }
        if (merged[key] || merged[key] === 0) {
          acc[key] = merged[key];
        }
        return acc;
      }, {}),
    };

    try {
      const res = await instance.get('/balance/balance-history', {
        params: query,
      });
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

const balancesSlice = createSlice({
  name: 'balances',
  initialState: {
    balances: [],
    count_total: 0,
    count_filtered: 0,
    params: balancesParams,
    search: null,
    filtersCount: 0,
    isInitialized: false,
    isLoading: true,
  },
  reducers: {
    setBalancesParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
      state.filtersCount = countFilters(state.params);
    },
    setBalancesSearch: (state, action) => {
      state.search = action.payload;
      state.params = { ...state.params, page: 1 };
      if (state.balances?.length === 0) {
        state.isInitialized = false;
      }
    },
    resetBalancesFilters: (state) => {
      state.params = {
        ...state.params,
        page: 1,
        ...balancesFilters,
        bank_id: state.params.bank_id,
      };
      state.filtersCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBalances.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchBalances.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isInitialized = true;
      state.balances = action.payload?.payments;
      state.count_total = action.payload?.count_total;
      state.count_filtered = action.payload?.count_filtered;
      state.filtersCount = countFilters(state.params);
    });
    builder.addCase(fetchBalances.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const { setBalancesParams, setBalancesSearch, resetBalancesFilters } =
  balancesSlice.actions;

export default balancesSlice.reducer;

export const balancesListener = createListenerMiddleware();

balancesListener.startListening({
  matcher: isAnyOf(setBalancesParams, setBalancesSearch, resetBalancesFilters),
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(fetchBalances());
  },
});
